import React from "react";
import {
  Card,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/ui/tabs"
import { Button } from "@/shared/ui/button"
import memberIcon from '@/images/SVG/member.svg';
import GeneralIcon from '@/images/SVG/generalcon.svg';
import billingIcon from '@/images/SVG/billingIcon.svg';

import { clearCache, handleLogout } from '@/features/auth/lib/authSession'
import ProfileGeneral from '@/features/profile/ProfileGeneral'
import ProfileMembers from '@/features/profile/ProfileMembers'
import ProfileBilling from '@/features/profile/ProfileBilling'
import ProfileTemplates from '@/features/profile/ProfileTemplates'
import ProfileVocabulary from '@/features/profile/ProfileVocabulary'
import ProfileTextModules from '@/features/profile/ProfileTextModules'
import ProfileData from '@/features/profile/ProfileData'
import { useDispatch, useSelector } from "react-redux";
import { selectActiveTab, setActiveTab } from "@/store/slices/ProfileSlice"
import VocabularyIcon from '@/images/SVG/vocabulary.svg'
import TextModuleIcon from '@/images/SVG/text-modules.svg'
import DataIcon from '@/images/SVG/database.svg'
import userPermissions from "@/shared/config/permissions"
import Loader from '@/shared/components/Loader'
import { useTranslation } from "react-i18next";
import { LayoutTemplate } from "lucide-react";
import { useSubscription } from "@/shared/hooks/useSubscription"
import { useEnsureUser } from "@/shared/hooks/useEnsureUser"
import {
  useGetMembersQuery,
  useGetSubstitutesQuery,
  useGetTransactionsQuery,
} from "@/api/rtk/profileApi"
import { useGetReportTemplatesQuery } from "@/api/rtk/reportsApi"

const Profile = () => {
  const { t } = useTranslation();
  const activeTab = useSelector(selectActiveTab);
  const dispatch = useDispatch();
  const { trialEnded } = useSubscription();

  const { user, userLoading } = useEnsureUser();

  const currentUserPermissions = userPermissions(user);
  const skipProfile = trialEnded;
  const skipAdmin = skipProfile || userLoading || !user?.is_admin;

  useGetSubstitutesQuery(undefined, { skip: skipProfile });
  useGetReportTemplatesQuery(undefined, { skip: skipProfile });
  useGetMembersQuery(undefined, { skip: skipAdmin });
  useGetTransactionsQuery(undefined, { skip: skipAdmin });

  const tabTrigger = [
    {
      title: t("profile.tabs.general"),
      icon: GeneralIcon,
      value: "general",
      component: ProfileGeneral,
    },
    {
      title: t("profile.tabs.members"),
      icon: memberIcon,
      value: "members",
      component: ProfileMembers,
    },
    {
      title: t("profile.tabs.billing"),
      icon: billingIcon,
      value: "billing",
      component: ProfileBilling,
    },
    {
      title: t("profile.tabs.templates"),
      iconSvg: <LayoutTemplate className="mr-3 w-5 h-5 text-[#525866]" />,
      value: "templates",
      component: ProfileTemplates,
    },
    {
      title: t("profile.tabs.vocabulary"),
      icon: VocabularyIcon,
      value: "vocabulary",
      component: ProfileVocabulary,
    },
    {
      title: t("profile.tabs.textModules"),
      icon: TextModuleIcon,
      value: "text-modules",
      component: ProfileTextModules,
    },
    {
      title: t("profile.tabs.data"),
      icon: DataIcon,
      value: "data",
      component: ProfileData,
    },

  ];

  if (userLoading) {
    return <div className="flex justify-center items-center h-[85vh] md:h-[630px] 2xl:h-[848px]">
      <Loader />
    </div>
  }

  return (
    <div className="mt-4 p-0 md:h-[630px] 2xl:h-[848px] md:p-6 mb-5 w-[90vw] rounded-3xl md:w-[80vw] md:bg-white md:border md:border-[#E4E4E7] md:shadow-[#0000000A]">
      <Card className="border-none shadow-none w-full h-full bg-transparent">
        <CardHeader className="p-0 mb-3 md:hidden">
          <CardTitle className="font-bold">{t("profile.title")}</CardTitle>
        </CardHeader>
        <Tabs
          value={activeTab}
          onValueChange={(value) => dispatch(setActiveTab(value))}
        >
          <div className="grid grid-rows-5 md:grid-cols-12 gap-5 2xl:gap-8 md:h-full w-full sticky">
            <div className="row-span-5 md:col-span-4 lg:col-span-3">
              <Card className="relative rounded-2xl">
                <TabsList
                  data-orientation="vertical"
                  className="flex flex-col p-[10px] gap-2 overflow-y-scroll md:overflow-hidden h-full justify-start bg-transparent dark:bg-transparent"
                >
                  {tabTrigger.map((tab, index) => {
                    const Icon = tab.iconSvg || (tab.icon ? <img src={tab.icon} className="mr-3 w-5 h-5" alt={tab.title} /> : null);
                    if (currentUserPermissions?.includes(tab.value)) {
                      return (
                        <TabsTrigger
                          key={index}
                          className="w-full justify-start data-[state=active]:bg-[#F6F8FA]"
                          value={tab.value}
                          onClick={() => dispatch(setActiveTab(tab.value))}
                        >
                          {Icon}
                          <span className="font-SuisseIntlLight font-semibold">{tab.title}</span>
                        </TabsTrigger>
                      );
                    }
                    return null;
                  })}
                </TabsList>
              </Card>
              <Button
                className="mt-4 w-full relative hidden md:inline-block"
                variant="outline"
                onClick={handleLogout}
              >
                {t("navigation.logout")}
              </Button>
              <Button
                className="mt-2 w-full relative hidden md:inline-block text-gray-400 hover:text-gray-500 hover:bg-slate-50"
                variant="ghost"
                onClick={clearCache}
              >
                {t("actions.clearCache")}
              </Button>
            </div>
            <div className="row-span-5 md:col-span-8 lg:col-span-9 md:max-w-[650px] 2xl:max-w-[720px] md:h-[575px] 2xl:h-[798px] overflow-auto no-scrollbar">
              {tabTrigger.map((tab) => (
                <TabsContent key={tab.value} value={tab.value}>
                  <tab.component />
                </TabsContent>
              ))}
            </div>
          </div>
        </Tabs>
      </Card>
    </div>
  );
};

export default Profile;